import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import HomeStore from '../../../store/HomeStore';
import ApiService from '../../../services/ApiService';

const MESSAGES = {
  expired: 'Your MYOB connection has expired. Please connect to MYOB again.',
  file_not_found: 'That company file is not available to your MYOB login. Please choose another one.',
  file_credentials: 'This company file needs its own username and password. Please enter them below.',
  file_unreachable: 'We could not open that company file. Please try again in a moment.',
};

const reasonOf = error => (error && error.response && error.response.data && error.response.data.reason) || '';

// Shown on /arliveapp after the MYOB callback: the user picks the company file ezyCollect will sync. A file that
// has its own username and password asks for them, and is opened with them before the form is shown.
const StepChooseMyobFile = ({ onChosen }) => {
  const [files, setFiles] = useState(null);
  const [fileId, setFileId] = useState('');
  const [needsCredentials, setNeedsCredentials] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ApiService.listMyobFiles(HomeStore.myobSignup)
      .then(response => {
        setFiles(response.data);
        if (response.data.length === 1) {
          setFileId(response.data[0].id);
        }
      })
      .catch(e => setError(reasonOf(e) || 'expired'));
  }, []);

  const choose = () => {
    setSaving(true);
    setError('');
    ApiService.selectMyobFile(HomeStore.myobSignup, fileId, needsCredentials ? username : null,
      needsCredentials ? password : null)
      .then(response => {
        HomeStore.setMyobFile(response.data.fileName);
        onChosen();
      })
      .catch(e => {
        const reason = reasonOf(e) || 'file_unreachable';
        if (reason === 'file_credentials') {
          setNeedsCredentials(true);
        }
        setError(reason);
      })
      .finally(() => setSaving(false));
  };

  if (error === 'expired') {
    return (
      <Grid container spacing={2} style={{ padding: '24px' }}>
        <Grid item xs={12} className="ez-text-center">
          <Typography variant="body1" gutterBottom>{MESSAGES.expired}</Typography>
          <Button variant="contained" color="primary" component={Link} to="/arlive">
            Connect to MYOB
          </Button>
        </Grid>
      </Grid>
    );
  }

  if (files === null) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Grid container spacing={2} style={{ padding: '24px' }}>
      <Grid item xs={12}>
        <Typography variant="body1" gutterBottom>
          Choose the company file ezyCollect will connect to:
        </Typography>
        {files.length === 0 ? (
          <Typography variant="body2" color="error">
            Your MYOB login has no AccountRight Live company files. Please sign in with another MYOB login.
          </Typography>
        ) : (
          <RadioGroup value={fileId} onChange={e => setFileId(e.target.value)}>
            {files.map(file => (
              <FormControlLabel key={file.id} value={file.id} control={<Radio color="primary" />} label={file.name} />
            ))}
          </RadioGroup>
        )}
      </Grid>
      {needsCredentials && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField label="Company file username" fullWidth value={username}
                       onChange={e => setUsername(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Company file password" type="password" fullWidth value={password}
                       onChange={e => setPassword(e.target.value)} />
          </Grid>
        </>
      )}
      {error && (
        <Grid item xs={12}>
          <Typography variant="body2" color="error">{MESSAGES[error] || MESSAGES.file_unreachable}</Typography>
        </Grid>
      )}
      <Grid item xs={12} className="ez-text-right">
        <Button variant="contained" color="primary" disabled={!fileId || saving} onClick={choose}>
          {saving ? 'Checking…' : 'Continue'}
        </Button>
      </Grid>
    </Grid>
  );
};

export default StepChooseMyobFile;
